import { createElement, dispatch } from "./misc";

const TIME_TRACK_DISPATCH_FACTOR = 60;

export function jwPlayerDataContext() {
  return {
    player: null as any,
    mediaId: null as any,

    audioTracks: null,
    currentAudioTrack: 0,
    preferredAudioTrack: 0,

    timeTrackReference: null,
    timeTrackCounter: TIME_TRACK_DISPATCH_FACTOR,

    async init() {
      const dataset = (this as any).$el.dataset;
      const playerId = String(dataset.jwPlayerId);
      const playerContainerId = String(dataset.jwPlayerContainerId);
      const playerInitialFullscreen = Boolean(dataset.jwInitialFullscreen);

      // The jwplayer library is loaded via an inline <script> next to this
      // element and may not be available yet when Alpine initializes this
      // component. Wait for it instead of crashing on "jwplayer is not a function".
      const jwplayer = await this.waitForJwPlayer();
      if (!jwplayer) {
        console.warn("[ymhJwPlayer] jwplayer library not available, skipping init");
        return;
      }

      this.player = jwplayer(playerContainerId);
      this.mediaId = String(dataset.jwMediaId);

      this.preferredAudioTrack = Math.max(0, Number(localStorage.getItem("preferredAudioTrack") ?? 0));

      // @see https://docs.jwplayer.com/players/reference/setup-options
      this.player.setup({
        // "playlist": String(dataset.jwPlaylist),
        playlist: [
          {
            file: String(dataset.jwFile),
            image: "https://cdn.jwplayer.com/v2/media/" + this.mediaId + "/poster.jpg?width=1280",
            tracks: [
              {
                file: "https://cdn.jwplayer.com/strips/" + this.mediaId + "-120.vtt",
                kind: "thumbnails",
              },
            ],
          },
        ],
      });

      if(playerInitialFullscreen) {
        this.player.setFullscreen(true);
      }

      this.player.on("ready", (a) => {
        const playerContainer = this.player.getContainer();

        screen.orientation?.addEventListener("change", (event) => {
          try {
            const so = event.target as ScreenOrientation;
            if (document.fullscreenElement === null) {
              const playerState = this.player?.getState() ?? "unknown";
              if (so?.type.startsWith("landscape") && playerState == "playing") {
                // Orientation-Change ist keine User-Geste — der Browser darf den
                // Request ablehnen (Promise-Rejection, vom try/catch nicht gefangen).
                playerContainer.requestFullscreen().catch(() => {});
              }
            } else if (document.fullscreenElement === playerContainer) {
              if (so?.type.startsWith("portrait")) {
                document.exitFullscreen().catch(() => {});
              }
            }
          } catch {
            // Ignore
          }
        });

        playerContainer.addEventListener("fullscreenchange", (event) => {
          if (event.target !== document.fullscreenElement) {
            setTimeout(() => event.target.scrollIntoView({ behavior: "smooth" }));
          }
        });
      });

      this.player.on("audioTracks", ($event) => this.refreshAudioTracks($event));
      this.player.on("audioTrackChanged", ($event) => this.refreshAudioTracks($event));

      this.player.on("play", () => {
        this.timeTrackReference = new Date();
        dispatch(window, "ymh-jw-player-play", {
          videoKey: this.mediaId,
          stopAndShowOverlay: (content: string) => this.stopAndShowOverlay(content),
        });
      });

      this.player.on("pause", () => {
        this.timeTrackReference = null;
        dispatch(window, "ymh-jw-player-pause", { videoKey: this.mediaId });
      });

      this.player.on("time", () => {
        if (this.timeTrackReference) {
          const currentTime = new Date();
          if (this.timeTrackCounter / TIME_TRACK_DISPATCH_FACTOR >= 1) {
            dispatch(window, "ymh-jw-player-track", { videoKey: this.mediaId });
          }
          this.timeTrackCounter %= TIME_TRACK_DISPATCH_FACTOR;
          this.timeTrackCounter += (Number(currentTime) - Number(this.timeTrackReference)) / 1000;
          this.timeTrackReference = currentTime;
        }
      });

      if ((this as any).$data.playerHolder !== undefined) {
        (this as any).$data.playerHolder = this;
      }
    },

    waitForJwPlayer(timeoutMs = 10000): Promise<any> {
      return new Promise((resolve) => {
        const ready = () => (typeof window["jwplayer"] === "function" ? window["jwplayer"] : null);
        const existing = ready();
        if (existing) {
          resolve(existing);
          return;
        }
        const start = Date.now();
        const interval = setInterval(() => {
          const jw = ready();
          if (jw) {
            clearInterval(interval);
            resolve(jw);
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(interval);
            resolve(null);
          }
        }, 50);
      });
    },

    refreshAudioTracks({ tracks, currentTrack, type }) {
      this.audioTracks = tracks;
      this.currentAudioTrack = currentTrack;
      if (type == "audioTracks") {
        const preferredAudioTrack = this.preferredAudioTrack;
        if (preferredAudioTrack == null) {
          return;
        }
        this.preferredAudioTrack = null;
        if (preferredAudioTrack !== this.currentAudioTrack && preferredAudioTrack < this.audioTracks.length) {
          (this as any).$nextTick(() => this.player.setCurrentAudioTrack(preferredAudioTrack));
        }
      }
    },

    cycleAudioTracks() {
      const nextAudioTrack = (this.currentAudioTrack + 1) % this.audioTracks.length;
      this.player.setCurrentAudioTrack(nextAudioTrack);
      localStorage.setItem("preferredAudioTrack", String(nextAudioTrack));
    },

    stopAndShowOverlay(content: string) {
      this.player.stop();
      ((this as any).$root as HTMLElement).prepend(
        createElement(
          `<div class="absolute inset-0 p-8 bg-white/80 flex place-items-center place-content-center text-center
              z-50 text-16 md:text-20 lg:text-24 leading-normal font-bold text-red">
            ${content}
          </div>`,
        ) ?? "",
      );
    },
  };
}
